import React, { Component, memo } from 'react';
import { Modal, ModalProps, Stepper } from '@app/components';
import { Step1 } from './components';
import { Doc, DocTypes } from '@app/models';
import { FilePickerForm, FormDoc } from '@app/forms';
import { Doc as DocComponent } from '@app/common';
import { createDoc } from '@app/api';

interface Props extends Partial<ModalProps> {
  contract: Doc;
  onCreate: (doc: Doc) => void;
}

interface State {
  docType: DocTypes | null;
  doc: Partial<Doc> | null;
  currentStep: number;
  maxCurrentStep: number;
  pending: boolean;
  files: File[];
}

class ModalAddDoc extends Component<Props, State> {
  steps = [
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 0 ? 0 : currentStep,
        }));
      },
      title: 'Выберите тип документа',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 1 ? 1 : currentStep,
        }));
      },
      title: 'Заполните данные договора',
    },
    {
      onClick: () => {
        this.setState(({ currentStep, maxCurrentStep }) => ({
          currentStep: maxCurrentStep >= 2 ? 2 : currentStep,
        }));
      },
      title: 'Загрузить документ',
    },
  ];

  state: State = {
    docType: null,
    doc: null,
    currentStep: 0,
    maxCurrentStep: 0,
    pending: false,
    files: [],
  };

  onClickClose = () => {
    const { onClose } = this.props;
    if (!!onClose) {
      onClose();
    }
  };

  onSubmitDocType = (docType: DocTypes) => {
    this.setState(({ maxCurrentStep }) => ({
      docType,
      currentStep: 1,
      maxCurrentStep: maxCurrentStep <= 1 ? 1 : maxCurrentStep,
    }));
  };

  onSubmitDoc = (doc: Partial<Doc>) => {
    this.setState(({ maxCurrentStep }) => ({
      doc,
      currentStep: 2,
      maxCurrentStep: maxCurrentStep <= 2 ? 2 : maxCurrentStep,
    }));
  };

  onSubmitFiles = (files: File[]) => {
    this.setState(
      {
        files,
        pending: true,
      },
      async () => {
        await this.createDoc();
      }
    );
  };

  createDoc = async () => {
    try {
      const { onClose, contract, onCreate } = this.props;
      const { docType, files, doc } = this.state;
      const params: Partial<Doc> = {
        type: docType!,
        contractId: contract.id,
        ...doc,
      };
      if (contract.companyId) {
        params.companyId = contract.companyId;
      }
      if (contract.recipientCompanyId) {
        params.recipientCompanyId = contract.recipientCompanyId;
      }
      if (contract.recipientId) {
        params.recipientId = contract.recipientId;
      }
      const docResponse = await createDoc(params, files);
      this.setState(
        {
          docType: null,
          doc: null,
          currentStep: 0,
          maxCurrentStep: 0,
          pending: false,
          files: [],
        },
        () => {
          onCreate(docResponse);
          onClose!();
        }
      );
    } catch (e) {
      this.setState({
        pending: false,
      });
    }
  };

  render() {
    const { visible, contract } = this.props;
    const { currentStep, maxCurrentStep, pending, docType } = this.state;
    if (!visible || !contract) {
      return null;
    }
    return (
      <Modal visible={visible} size="xlarge">
        <div className="bg-card rounded-lg p-0 mb-4">
          <Stepper
            onClose={this.onClickClose}
            title="Добавить документ"
            steps={this.steps}
            currentStep={currentStep}
            maxCurrentStep={maxCurrentStep}
            fullWidth={true}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div className="bg-card rounded-lg p-4 shadow">
            <div className="font-semibold mb-2">Описание документ</div>
            <DocComponent
              doc={contract}
              hideStatus={true}
              variant="secondary"
            />
          </div>
          {currentStep === 0 && <Step1 onSubmit={this.onSubmitDocType} />}
          {currentStep === 1 && (
            <FormDoc onSubmit={this.onSubmitDoc} type={docType!} />
          )}
          {currentStep === 2 && (
            <FilePickerForm
              accept="application/pdf"
              pending={pending}
              defaultExpanded={currentStep === 2}
              onSubmit={this.onSubmitFiles}
              required={true}
            />
          )}
        </div>
      </Modal>
    );
  }
}

export default memo(ModalAddDoc);
